import { createFileRoute, Link } from "@tanstack/react-router";
import { trpc, RouterOutputs } from "@/hooks/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { PlusIcon, Trash2Icon } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import type { EquipmentModelId, CleaningProcedureId } from "@brewbase/db/types";
import { RequireCapability } from "@/components/require-capability";

export const Route = createFileRoute("/settings/inventory/equipment-models")({
  component: () => (
    <RequireCapability cap="inventory:manage">
      <RouteComponent />
    </RequireCapability>
  ),
});

function RouteComponent() {
  const utils = trpc.useUtils();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [filterTypeId, setFilterTypeId] = useState<string>("all");

  const { data: models, isLoading } = trpc.equipment.models.list.useQuery(
    filterTypeId && filterTypeId !== "all"
      ? { equipmentTypeId: parseInt(filterTypeId) as RouterOutputs["equipment"]["types"]["list"][number]["id"] }
      : undefined,
  );
  const { data: types } = trpc.equipment.types.list.useQuery();
  const { data: procedures } = trpc.cleaning.procedures.list.useQuery(undefined);

  const updateMutation = trpc.equipment.models.update.useMutation({
    onSuccess: () => {
      utils.equipment.models.list.invalidate();
    },
    onError: (err) => {
      toast.error(`Failed to update: ${err.message}`);
    },
  });

  const deleteMutation = trpc.equipment.models.delete.useMutation({
    onSuccess: () => {
      toast.success("Model deleted");
      utils.equipment.models.list.invalidate();
    },
    onError: (err) => {
      toast.error(`Failed to delete: ${err.message}`);
    },
  });

  if (isLoading) {
    return <div className="container mx-auto px-4 py-6">Loading...</div>;
  }

  return (
    <div className="container mx-auto px-4 py-6 flex flex-col grow min-h-0 gap-4">
      <div>
        <h2 className="text-lg font-medium">Equipment Models</h2>
        <p className="text-sm text-muted-foreground">
          Models define specific makes/models of equipment within each type.
        </p>
      </div>

      <div className="flex gap-2 items-center">
        <Select value={filterTypeId} onValueChange={setFilterTypeId}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by type..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {types?.map((type) => (
              <SelectItem key={type.id} value={String(type.id)}>
                {type.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button onClick={() => setIsCreateOpen(true)}>
          <PlusIcon className="h-4 w-4 mr-2" />
          Add Model
        </Button>
      </div>

      {models && models.length > 0 ? (
        <div className="space-y-2">
          {models.map((model) => (
            <div
              key={model.id}
              className="flex items-center gap-4 p-3 border border-olive-6"
            >
              <Input
                defaultValue={model.name}
                onBlur={(e) => {
                  if (e.target.value !== model.name) {
                    updateMutation.mutate({ id: model.id, name: e.target.value });
                  }
                }}
                className="flex-1"
              />
              <span className="text-sm text-muted-foreground w-32">
                {model.typeName}
              </span>
              <Input
                defaultValue={model.manufacturer ?? ""}
                placeholder="Manufacturer"
                onBlur={(e) => {
                  if (e.target.value !== (model.manufacturer ?? "")) {
                    updateMutation.mutate({
                      id: model.id,
                      manufacturer: e.target.value || null,
                    });
                  }
                }}
                className="w-40"
              />
              <Input
                defaultValue={model.model_number ?? ""}
                placeholder="Model #"
                onBlur={(e) => {
                  if (e.target.value !== (model.model_number ?? "")) {
                    updateMutation.mutate({
                      id: model.id,
                      modelNumber: e.target.value || null,
                    });
                  }
                }}
                className="w-32"
              />
              <CleaningProcedureSelect
                label="Pre-cleaning"
                value={model.pre_cleaning_procedure_id ?? null}
                procedures={procedures ?? []}
                onChange={(id) =>
                  updateMutation.mutate({ id: model.id, preCleaningProcedureId: id })
                }
              />
              <CleaningProcedureSelect
                label="Post-cleaning"
                value={model.post_cleaning_procedure_id ?? null}
                procedures={procedures ?? []}
                onChange={(id) =>
                  updateMutation.mutate({ id: model.id, postCleaningProcedureId: id })
                }
              />
              <Link
                to="/brewcontrol/wirings/$modelId"
                params={{ modelId: String(model.id) }}
                className="text-xs text-olive-11 hover:text-olive-12 whitespace-nowrap"
                title="The wiring every vessel of this model shares"
              >
                Wiring
              </Link>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => deleteMutation.mutate(model.id)}
              >
                <Trash2Icon className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">No equipment models defined.</p>
      )}

      <CreateModelDialog
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        types={types ?? []}
      />
    </div>
  );
}

// ==================== Cleaning Procedure Select ====================

type Procedure = RouterOutputs["cleaning"]["procedures"]["list"][number];

interface CleaningProcedureSelectProps {
  label: string;
  value: CleaningProcedureId | null;
  procedures: Procedure[];
  onChange: (id: CleaningProcedureId | null) => void;
}

function CleaningProcedureSelect({
  label,
  value,
  procedures,
  onChange,
}: CleaningProcedureSelectProps) {
  return (
    <div className="flex flex-col gap-1 w-48">
      <span className="text-xs text-muted-foreground">{label}</span>
      <Select
        value={value ? String(value) : "__none__"}
        onValueChange={(v) =>
          onChange(v === "__none__" ? null : (parseInt(v) as CleaningProcedureId))
        }
      >
        <SelectTrigger className="h-8 text-sm">
          <SelectValue placeholder="None" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">None</SelectItem>
          {procedures.map((p) => (
            <SelectItem key={p.id} value={String(p.id)}>
              {p.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// ==================== Create Model Dialog ====================

interface CreateModelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  types: RouterOutputs["equipment"]["types"]["list"];
}

function CreateModelDialog({ open, onOpenChange, types }: CreateModelDialogProps) {
  const utils = trpc.useUtils();
  const [name, setName] = useState("");
  const [typeId, setTypeId] = useState<string>("");
  const [manufacturer, setManufacturer] = useState("");
  const [modelNumber, setModelNumber] = useState("");

  const createMutation = trpc.equipment.models.create.useMutation({
    onSuccess: () => {
      toast.success("Model created");
      utils.equipment.models.list.invalidate();
      onOpenChange(false);
      setName("");
      setTypeId("");
      setManufacturer("");
      setModelNumber("");
    },
    onError: (err) => {
      toast.error(`Failed to create: ${err.message}`);
    },
  });

  const handleCreate = () => {
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!typeId) {
      toast.error("Type is required");
      return;
    }
    createMutation.mutate({
      name,
      equipmentTypeId: parseInt(typeId) as RouterOutputs["equipment"]["types"]["list"][number]["id"],
      manufacturer: manufacturer || null,
      modelNumber: modelNumber || null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Equipment Model</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Model name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="type">Equipment Type</Label>
            <Select value={typeId} onValueChange={setTypeId}>
              <SelectTrigger>
                <SelectValue placeholder="Select type..." />
              </SelectTrigger>
              <SelectContent>
                {types.map((type) => (
                  <SelectItem key={type.id} value={String(type.id)}>
                    {type.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="manufacturer">Manufacturer</Label>
              <Input
                id="manufacturer"
                value={manufacturer}
                onChange={(e) => setManufacturer(e.target.value)}
                placeholder="Manufacturer"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="modelNumber">Model Number</Label>
              <Input
                id="modelNumber"
                value={modelNumber}
                onChange={(e) => setModelNumber(e.target.value)}
                placeholder="Model #"
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={createMutation.isPending}>
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
